<template>
  <section
    v-if="!isClosed && !isExpired"
    :class="{
      'closable-banner-section': true,
    }"
  >
    <GeneralTheme :theme="props.configuration">
      <div :class="{ 'fluid-container mx-auto': true }">
        <div
          :class="{
            'mx-auto flex rounded-10px border-2 border-solid border-www-color-accent-purple shadow-closable-banner lg:max-w-max': true,
          }"
        >
          <div
            :class="{
              'closable-banner-section_content-wrapper relative': true,
              'flex w-full flex-col justify-start gap-10px px-30px py-30px lg:flex-row lg:items-center lg:gap-5 lg:px-10 lg:pb-14px lg:pt-13px': true,
            }"
          >
            <div
              :class="{ 'mb-10px flex items-center text-www-color-accent-purple lg:mb-0': true }"
            >
              <PtcIcon
                v-if="props.icon"
                :class="{ 'text-28px leading-7': true }"
                :name="props.icon"
              />
              <span :class="{ 'ml-5 text-xl font-bold': true }">{{
                props.title?.toLocaleUpperCase()
              }}</span>
            </div>

            <PortableText
              v-if="props.bannerContent?.standardPortableText"
              :portable-data="props.bannerContent.standardPortableText"
              :class="{
                'flex-grow text-www-color-neutral-medium-dark': true,
              }"
            />
            <button
              :class="{
                'align-center flex h-6 w-6 min-w-6 cursor-pointer items-center justify-center rounded-full border border-solid border-www-color-neutral-medium-dark p-1 text-sm hover:shadow-lg lg:ml-5': true,
                'absolute right-30px top-32px translate-y-3px lg:relative lg:right-auto lg:top-auto lg:translate-y-0': true,
              }"
              @click="handleClose"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
              >
                <path
                  d="M0.209802 0.209802C0.276139 0.143298 0.354944 0.0905335 0.441704 0.0545322C0.528463 0.0185308 0.621474 0 0.715406 0C0.809339 0 0.902349 0.0185308 0.989109 0.0545322C1.07587 0.0905335 1.15467 0.143298 1.22101 0.209802L5.00019 3.99041L8.77936 0.209802C8.84576 0.143405 8.92459 0.0907363 9.01134 0.0548025C9.09809 0.0188688 9.19107 0.000373895 9.28497 0.000373895C9.37887 0.000373895 9.47185 0.0188688 9.5586 0.0548025C9.64535 0.0907363 9.72418 0.143405 9.79057 0.209802C9.85697 0.276199 9.90964 0.355023 9.94557 0.441775C9.98151 0.528527 10 0.621507 10 0.715406C10 0.809306 9.98151 0.902286 9.94557 0.989038C9.90964 1.07579 9.85697 1.15461 9.79057 1.22101L6.00997 5.00019L9.79057 8.77936C9.85697 8.84576 9.90964 8.92459 9.94557 9.01134C9.98151 9.09809 10 9.19107 10 9.28497C10 9.37887 9.98151 9.47185 9.94557 9.5586C9.90964 9.64535 9.85697 9.72418 9.79057 9.79057C9.72418 9.85697 9.64535 9.90964 9.5586 9.94557C9.47185 9.98151 9.37887 10 9.28497 10C9.19107 10 9.09809 9.98151 9.01134 9.94557C8.92459 9.90964 8.84576 9.85697 8.77936 9.79057L5.00019 6.00997L1.22101 9.79057C1.15461 9.85697 1.07579 9.90964 0.989038 9.94557C0.902286 9.98151 0.809306 10 0.715406 10C0.621507 10 0.528527 9.98151 0.441775 9.94557C0.355023 9.90964 0.276199 9.85697 0.209802 9.79057C0.143405 9.72418 0.0907363 9.64535 0.0548025 9.5586C0.0188688 9.47185 0.000373895 9.37887 0.000373895 9.28497C0.000373895 9.19107 0.0188688 9.09809 0.0548025 9.01134C0.0907363 8.92459 0.143405 8.84576 0.209802 8.77936L3.99041 5.00019L0.209802 1.22101C0.143298 1.15467 0.0905335 1.07587 0.0545322 0.989109C0.0185308 0.902349 0 0.809339 0 0.715406C0 0.621474 0.0185308 0.528463 0.0545322 0.441704C0.0905335 0.354944 0.143298 0.276139 0.209802 0.209802Z"
                  fill="#3D4647"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </GeneralTheme>
  </section>
</template>

<script lang="ts" setup>
import PortableText from '~/components/Atmos/PortableText/PortableText.vue';
import GeneralTheme from '~/components/Molecules/Themes/GeneralTheme.vue';
import PtcIcon from '~/components/Atmos/PtcIcon/PtcIcon.vue';
import type { ClosableBanner } from '~/sanity/types';

const props = defineProps<ClosableBanner>();

const { setToLocalStorage, getFromLocalStorageOrNull } = useWwwStorage();

const { dataLayerPush, mouseFlowPush } = useTracking();

const { query, path } = useRoute();

const closedBanners = ref(getFromLocalStorageOrNull<string[]>({ key: 'ClosedBanners' }) || []);

const isClosed = computed({
  get: () => {
    const key = props.link[0]?._key;
    return key ? closedBanners.value.includes(key) : false;
  },
  set: (value: boolean) => {
    if (!value) return;
    const key = props.link[0]?._key;
    if (key && !closedBanners.value.includes(key)) {
      closedBanners.value.push(key);
      setToLocalStorage({
        key: 'ClosedBanners',
        data: closedBanners.value,
        ttl: Date.now() + 7 * 24 * 60 * 60 * 1000,
      });
    }
    return value;
  },
});

const handleClose = () => {
  isClosed.value = true;

  dataLayerPush({
    event: 'closable_banner_closed',
    banner_title: props.title || '',
    banner_key: props.link[0]?._key || '',
    banner_page: path,
  });

  mouseFlowPush(['tag', 'closable-banner-hidden']);
};

onMounted(() => {
  if (!isClosed.value) {
    mouseFlowPush(['tag', 'closable-banner-shown']);
  }
});

const hasCloseParam = computed(() => {
  return query['cbs'] === 'close-all';
});
// if we see close in url params, close the banner
if (hasCloseParam.value) {
  isClosed.value = true;
  const key = props.link[0]?._key;
  if (key && !closedBanners.value.includes(key)) {
    closedBanners.value.push(key);
    setToLocalStorage({
      key: 'ClosedBanners',
      data: closedBanners.value,
      ttl: Date.now() + 7 * 24 * 60 * 60 * 1000,
    });
  }
}

const hasUncloseParam = computed(() => {
  return query['cbs'] === 'unclose-all';
});
// if we see unclose in url params, unclose the banner
if (hasUncloseParam.value) {
  isClosed.value = false;
  // also clear local storage, check length so we only do once per page load
  if (closedBanners.value.length > 0) {
    closedBanners.value = [];
    setToLocalStorage({
      key: 'ClosedBanners',
      data: closedBanners.value,
      ttl: Date.now() + 7 * 24 * 60 * 60 * 1000,
    });
  }
}

const isExpired = computed(() => {
  if (!props.expiryDate || hasUncloseParam.value) return false;
  const now = new Date();
  const expiry = new Date(props.expiryDate);
  return now > expiry;
});
</script>
<style lang="scss">
.closable-banner-section {
  &_content-wrapper {
    .text-one-column .container {
      max-width: none !important;
    }
    p {
      margin-bottom: 0 !important;
      font-size: 1.25rem !important;
      line-height: 1.75rem !important;
      font-weight: 500 !important;
      a {
        color: var(--www-color-accent-purple) !important;
        text-decoration: none !important;
        margin-left: 1.25rem;
        font-weight: 700;
        @media (max-width: 1024px) {
          margin-left: 0;
          display: block !important;
        }
        &:hover {
          color: var(--www-color-accent-purple) !important;
        }
      }
    }
  }
}
</style>
